import { ArrowDown, ArrowUp, ArrowDownUp } from 'lucide-react';
import { useState } from 'react';
import type { InvestmentPortfolioHolding } from 'shared';

import { formatCurrency } from '../../lib/format/currency';
import { Badge } from '../ui/Badge';
import { Table } from '../ui/Table';
import { TableCell } from '../ui/TableCell';
import { TableHeaderCell } from '../ui/TableHeaderCell';

type SortKey = 'symbol' | 'value' | 'weightPct' | 'unrealizedPnlPct' | 'totalReturnPct';

function money(value: string | null, currency: string) {
  return value === null ? '—' : formatCurrency(value, currency);
}

function pct(value: string | null) {
  return value === null ? '—' : `${Number(value).toFixed(1)}%`;
}

function tone(value: string | null): 'danger' | 'neutral' | 'success' {
  if (value === null || Number(value) === 0) return 'neutral';
  return Number(value) > 0 ? 'success' : 'danger';
}

function sortValue(holding: InvestmentPortfolioHolding, key: SortKey): number | null {
  const raw = key === 'symbol' ? null : holding[key];
  return raw === null ? null : Number(raw);
}

function sortHoldings(
  holdings: readonly InvestmentPortfolioHolding[],
  key: SortKey,
  descending: boolean,
): InvestmentPortfolioHolding[] {
  const direction = descending ? -1 : 1;
  return [...holdings].sort((left, right) => {
    if (key === 'symbol') return left.symbol.localeCompare(right.symbol) * direction;
    const leftValue = sortValue(left, key);
    const rightValue = sortValue(right, key);
    // Unavailable values always sink to the bottom regardless of direction.
    if (leftValue === null && rightValue === null) return left.symbol.localeCompare(right.symbol);
    if (leftValue === null) return 1;
    if (rightValue === null) return -1;
    const delta = leftValue - rightValue;
    return delta === 0 ? left.symbol.localeCompare(right.symbol) : delta * direction;
  });
}

function signals(holding: InvestmentPortfolioHolding): string[] {
  return [
    holding.missingThesisCount > 0 ? `No thesis ×${holding.missingThesisCount}` : null,
    holding.reviewDueCount > 0 ? `Review due ×${holding.reviewDueCount}` : null,
    holding.brokenThesisCount > 0 ? `Broken ×${holding.brokenThesisCount}` : null,
    holding.aboveMaximumCount > 0 ? `Above max ×${holding.aboveMaximumCount}` : null,
  ].filter((signal): signal is string => signal !== null);
}

export function InvestmentHoldingsTable({
  holdings,
  currency,
  onSelectAccount,
}: {
  holdings: readonly InvestmentPortfolioHolding[];
  currency: string;
  onSelectAccount: (accountId: string) => void;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('value');
  const [descending, setDescending] = useState(true);
  const rows = sortHoldings(holdings, sortKey, descending);

  function toggleSort(key: SortKey): void {
    if (key === sortKey) {
      setDescending((value) => !value);
      return;
    }
    setSortKey(key);
    setDescending(key !== 'symbol');
  }

  function header(key: SortKey, label: string, numeric = true) {
    const active = key === sortKey;
    const Icon = active ? (descending ? ArrowDown : ArrowUp) : ArrowDownUp;
    return (
      <TableHeaderCell
        numeric={numeric}
        aria-sort={active ? (descending ? 'descending' : 'ascending') : 'none'}
        className="whitespace-nowrap"
      >
        <button
          type="button"
          onClick={() => toggleSort(key)}
          className="inline-flex items-center gap-1 uppercase hover:text-text"
          aria-label={`Sort by ${label}`}
        >
          {label}
          <Icon className={`h-3 w-3 ${active ? 'text-text' : ''}`} aria-hidden="true" />
        </button>
      </TableHeaderCell>
    );
  }

  return (
    <div className="overflow-x-auto rounded border border-border">
      <Table className="min-w-[760px] text-sm" aria-label="Combined holdings list">
        <thead className="bg-surface-raised">
          <tr>
            {header('symbol', 'Holding', false)}
            <TableHeaderCell>Theme</TableHeaderCell>
            {header('value', 'Value')}
            {header('weightPct', 'Weight')}
            {header('unrealizedPnlPct', 'Unrealised')}
            {header('totalReturnPct', 'Total return')}
            <TableHeaderCell>Accounts</TableHeaderCell>
            <TableHeaderCell>Signals</TableHeaderCell>
          </tr>
        </thead>
        <tbody>
          {rows.map((holding) => {
            const holdingSignals = signals(holding);
            return (
              <tr key={holding.identity} className="border-t border-border hover:bg-surface-raised">
                <TableCell className="max-w-[14rem] py-1.5">
                  <div className="font-semibold">{holding.symbol}</div>
                  <div className="truncate text-xs text-text-muted" title={holding.stock}>
                    {holding.stock}
                  </div>
                </TableCell>
                <TableCell className="py-1.5 text-text-muted">
                  {holding.theme ?? 'Unclassified'}
                </TableCell>
                <TableCell numeric className="py-1.5">
                  {money(holding.value, currency)}
                </TableCell>
                <TableCell numeric className="py-1.5">
                  {pct(holding.weightPct)}
                </TableCell>
                <TableCell numeric className="whitespace-nowrap py-1.5">
                  <Badge tone={tone(holding.unrealizedPnl)}>
                    {money(holding.unrealizedPnl, currency)} · {pct(holding.unrealizedPnlPct)}
                  </Badge>
                </TableCell>
                <TableCell numeric className="whitespace-nowrap py-1.5">
                  {holding.totalReturn === null ? (
                    <span className="text-xs text-text-muted">History incomplete</span>
                  ) : (
                    <Badge tone={tone(holding.totalReturn)}>
                      {money(holding.totalReturn, currency)} · {pct(holding.totalReturnPct)}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="py-1.5">
                  <div className="flex flex-wrap gap-x-2 gap-y-1">
                    {holding.accounts.map((account) => (
                      <button
                        key={account.holdingId}
                        type="button"
                        onClick={() => onSelectAccount(account.accountId)}
                        className="whitespace-nowrap text-left underline hover:decoration-primary"
                        aria-label={`Open ${account.accountName} for ${holding.symbol}: ${money(account.value, currency)}`}
                      >
                        {account.accountName}
                      </button>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="py-1.5">
                  {holdingSignals.length === 0 ? (
                    <span className="text-xs text-text-muted">OK</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {holdingSignals.map((signal) => (
                        <Badge key={signal} tone="warning">
                          {signal}
                        </Badge>
                      ))}
                    </div>
                  )}
                </TableCell>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}
